import React, { useState } from 'react';
import { motion } from 'framer-motion';

const contactInfo = [
  {
    icon: '📱',
    label: 'Phone',
    value: '7993305226',
    href: 'tel:7993305226',
    description: 'Call us to place an order or make a reservation',
  },
  {
    icon: '✉️',
    label: 'Email',
    value: 'akulamadhuhasini@gmail.com',
    href: 'mailto:akulamadhuhasini@gmail.com',
    description: 'Send us an email for inquiries and feedback',
  },
  {
    icon: '📍',
    label: 'Address',
    value: 'Near Kartigi Bus Stop',
    href: '#',
    description: 'Visit us at our cozy location',
  },
  {
    icon: '🕐',
    label: 'Hours',
    value: '8 AM – 10 PM',
    href: '#',
    description: 'Open every day, Monday through Sunday',
  },
];

const socialLinks = [
  {
    name: 'Facebook',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
      </svg>
    ),
    href: '#',
    color: 'hover:bg-blue-500',
  },
  {
    name: 'Instagram',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    ),
    href: '#',
    color: 'hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500',
  },
  {
    name: 'LinkedIn',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
        <circle cx="4" cy="4" r="2" />
      </svg>
    ),
    href: '#',
    color: 'hover:bg-blue-600',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="bg-cream-50">
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/hero.png"
            alt="Contact Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-espresso-950/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-espresso-950/90 to-transparent" />
        </div>
        <div className="relative z-10 text-center px-4">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="category-badge mb-6 inline-flex bg-white/10 border-white/20 text-white/80"
          >
            <span>💬</span> Get in Touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Contact <span className="text-gradient-gold">Us</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-white/70 text-lg max-w-lg mx-auto"
          >
            We'd love to hear from you — reach out anytime!
          </motion.p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {contactInfo.map((info, idx) => (
              <motion.a
                key={info.label}
                href={info.href}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="glass-card p-6 text-center group hover:shadow-xl transition-all duration-500 block"
                id={`contact-card-${info.label.toLowerCase()}`}
              >
                <div className="text-4xl mb-4 group-hover:scale-125 transition-transform duration-500">
                  {info.icon}
                </div>
                <div className="text-xs uppercase tracking-wider text-espresso-400 mb-2">
                  {info.label}
                </div>
                <div className="font-semibold text-espresso-900 group-hover:text-amber-600 transition-colors duration-300 text-sm break-all">
                  {info.value}
                </div>
                <p className="text-espresso-400 text-xs mt-2">{info.description}</p>
              </motion.a>
            ))}
          </div>

          {/* Main Content: Form + Map */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="divider-gold mb-6" />
              <h2
                className="text-3xl font-bold text-espresso-900 mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Send Us a <span className="text-gradient-gold">Message</span>
              </h2>
              <p className="text-espresso-400 mb-8">
                Have a question or feedback? Fill out the form and we'll get back to you.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" id="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-espresso-700 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="w-full px-4 py-3 rounded-xl bg-white border border-espresso-100 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-all duration-300 text-espresso-900 placeholder:text-espresso-300 outline-none"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-espresso-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="w-full px-4 py-3 rounded-xl bg-white border border-espresso-100 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-all duration-300 text-espresso-900 placeholder:text-espresso-300 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-espresso-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="How can we help?"
                    className="w-full px-4 py-3 rounded-xl bg-white border border-espresso-100 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-all duration-300 text-espresso-900 placeholder:text-espresso-300 outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-espresso-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Tell us what's on your mind..."
                    className="w-full px-4 py-3 rounded-xl bg-white border border-espresso-100 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition-all duration-300 text-espresso-900 placeholder:text-espresso-300 outline-none resize-none"
                  />
                </div>

                <button
                  type="submit"
                  id="contact-submit"
                  className="btn-primary w-full sm:w-auto"
                >
                  {submitted ? '✅ Message Sent!' : '✉️ Send Message'}
                </button>
              </form>
            </motion.div>

            {/* Right Side: Map + Social */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="space-y-8"
            >
              {/* Map Embed */}
              <div className="rounded-2xl overflow-hidden shadow-lg h-[350px] bg-espresso-100">
                <iframe
                  title="Squeeze N Sip Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3844.123456789!2d76.123456789!3d15.123456789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sKartigi+Bus+Stop!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="grayscale hover:grayscale-0 transition-all duration-700"
                />
              </div>

              {/* Social Media */}
              <div className="glass-card p-8">
                <h3
                  className="text-xl font-bold text-espresso-900 mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Follow Us on <span className="text-gradient-gold">Social Media</span>
                </h3>
                <div className="flex gap-4">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      id={`social-${social.name.toLowerCase()}`}
                      className={`w-12 h-12 rounded-xl bg-espresso-100 flex items-center justify-center text-espresso-500 ${social.color} hover:text-white transition-all duration-300 group`}
                      aria-label={social.name}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
                <p className="text-espresso-400 text-sm mt-4">
                  Stay updated with our latest offerings, events, and behind-the-scenes moments!
                </p>
              </div>

              {/* Quick Facts */}
              <div className="bg-warm-gradient rounded-2xl p-8 text-white">
                <h3
                  className="text-xl font-bold mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Why Visit <span className="text-gradient-gold">Squeeze N Sip?</span>
                </h3>
                <ul className="space-y-3">
                  {[
                    '☕ Handcrafted premium beverages',
                    '🍕 Fresh & delicious snacks',
                    '🌿 Healthy menu options',
                    '🧋 Trending shakes & coolers',
                    '🏠 Cozy & Instagram-worthy ambiance',
                    '😊 Friendly & welcoming service',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-white/80 text-sm">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
